/**
 * Lightweight date-range picker with preset chips + Custom inputs.
 *
 * Usage:
 *   const [range, setRange] = useState(getPresetRange('ytd'));
 *   <DateRangePicker value={range} onChange={setRange} />
 *
 * Value shape: { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD', preset: '...' }.
 */
import { useMemo } from 'react';
import { Calendar } from '@phosphor-icons/react';
import { Input } from '../ui/input';

const PRESETS = [
  { id: 'ytd',       label: 'YTD' },
  { id: 'this_month',label: 'This Month' },
  { id: 'last_month',label: 'Last Month' },
  { id: 'last_7',    label: 'Last 7d' },
  { id: 'custom',    label: 'Custom' },
];

const _iso = (d) => d.toISOString().slice(0, 10);

export function getPresetRange(id) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (id) {
    case 'this_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: _iso(start), end: _iso(today), preset: id };
    }
    case 'last_month': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: _iso(start), end: _iso(end), preset: id };
    }
    case 'last_7': {
      const start = new Date(today); start.setDate(start.getDate() - 6);
      return { start: _iso(start), end: _iso(today), preset: id };
    }
    case 'ytd':
    default: {
      const start = new Date(today.getFullYear(), 0, 1);
      return { start: _iso(start), end: _iso(today), preset: 'ytd' };
    }
  }
}

export default function DateRangePicker({ value, onChange, className = '' }) {
  // Derive immediate values from the controlled `value` prop instead of
  // mirroring it through useEffect (which trips the eslint
  // react-hooks/set-state-in-effect rule).
  const preset = value?.preset || 'ytd';
  const start  = value?.start  || getPresetRange('ytd').start;
  const end    = value?.end    || getPresetRange('ytd').end;

  const presetClicked = (id) => {
    if (id !== 'custom') {
      const r = getPresetRange(id);
      onChange?.(r);
    } else {
      onChange?.({ start, end, preset: 'custom' });
    }
  };

  const onStart = (e) => {
    onChange?.({ start: e.target.value, end, preset: 'custom' });
  };
  const onEnd = (e) => {
    onChange?.({ start, end: e.target.value, preset: 'custom' });
  };

  const showCustom = useMemo(() => preset === 'custom', [preset]);

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="date-range-picker">
      <Calendar size={14} className="text-[#78716C]" />
      <div className="flex flex-wrap gap-1">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => presetClicked(p.id)}
            data-testid={`range-preset-${p.id}`}
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider transition ${
              preset === p.id
                ? 'bg-[#0A2540] text-white'
                : 'bg-white text-[#0A2540] border border-[#EDE6D8] hover:bg-[#FCFAF6]'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {showCustom && (
        <div className="flex items-center gap-1 ml-1" data-testid="range-custom-inputs">
          <Input type="date" value={start} onChange={onStart} className="h-7 text-xs px-2 w-[140px]" />
          <span className="text-[#78716C] text-xs">→</span>
          <Input type="date" value={end} onChange={onEnd} className="h-7 text-xs px-2 w-[140px]" />
        </div>
      )}
    </div>
  );
}
